import { Button, Flex, Link, Text } from '@chakra-ui/react';
import { AxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useUserAuth } from 'src/AppModule/hooks';
import { useI18n } from 'src/i18n';
import { Utils } from 'src/shared-hold/utils';
import { API, APIModels } from 'src/shared/api';
import { SpinnerLoading } from 'src/shared/components';
import { useToastVariants } from 'src/shared/hooks';
import { AppLocalStorage } from 'src/shared/utils/appLocalStorage/appLocalStorage';

const UnAuthGuard: React.FC = () => {
  const { translate } = useI18n();
  const redirect = useCallback(() => <Navigate to="/home" replace />, []);
  const { isSignedIn, signIn } = useUserAuth();
  const { toastInfo, toast } = useToastVariants();
  const toastId = 'user-forum-registered';
  const [authError, setAuthError] = useState<string | null>(null);
  const hasRedirectedToLandingPage = useRef(false);

  useEffect(() => {
    if (isSignedIn) {
      return;
    }

    const jiraToken = Utils.Url.getUrlParam('jiraToken');
    const stayConnected = Utils.Url.getUrlParam('stayConnected');
    const tokenUrl = Utils.Url.getUrlParam('token');
    const wasForumRegistered = Utils.Url.getUrlParam('wasForumRegistered');
    if (!tokenUrl) {
      redirectToLandingPage();
      return;
    }

    removeSensitiveParamsFromUrl();
    AppLocalStorage.setItem('APP:SESSION:STAY_CONNECTED', !!stayConnected, 'localStorage');

    const { response } = API.Users.getLoggedUser({ headers: { Token: tokenUrl } });
    response
      .then((data) => {
        if (wasForumRegistered === 'true') {
          showToastForumRegistered();
        }
        if (data.info.isNecessaryCompleteAddress) {
          showToastCompleteAddress();
        }
        return setLoggedUser(data, !!stayConnected, tokenUrl, jiraToken);
      })
      .catch((error: AxiosError) => {
        if (error.response?.status === APIModels.StatusCodes.unauthorized) {
          redirectToLandingPage();
          return;
        }

        setAuthError('Falha ao validar o token de acesso.');
      });
  }, []);

  function setLoggedUser(
    data: APIModels.ILoggedUserDTO,
    stayConnected: boolean,
    token: string,
    jiraToken: string | null
  ) {
    signIn({
      id: data.id,
      idCompany: data.idCompany,
      companyName: data.companyName,
      name: data.name,
      type: data.type,
      auth: {
        jiraToken,
        token,
        stayConnected
      },
      info: {
        isNecessaryToAnswerSurvey: data.info.isNecessaryToAnswerSurvey,
        hasAccessToNda: data.info.hasAccessToNda
      }
    });
  }

  function redirectToLandingPage() {
    const landingPage = process.env.REACT_APP_LANDING_PAGE;
    if (!landingPage) {
      setAuthError('REACT_APP_LANDING_PAGE nao esta configurado.');
      return;
    }

    if (hasRedirectedToLandingPage.current) {
      setAuthError('Token de autenticacao ausente. Abra o portal pela landing page correta.');
      return;
    }

    hasRedirectedToLandingPage.current = true;
    window.location.assign(getLandingLoginUrl(landingPage));
  }

  function getLandingLoginUrl(landingPage: string) {
    const url = new URL(landingPage, window.location.origin);
    url.pathname = `${url.pathname.replace(/\/$/, '')}/login`;
    url.search = 'shouldInvalidateToken=true';
    return url.toString();
  }

  function removeSensitiveParamsFromUrl() {
    const url = new URL(window.location.href);
    ['token', 'jiraToken', 'stayConnected', 'wasForumRegistered'].forEach((param) => url.searchParams.delete(param));
    window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
  }

  function showToastForumRegistered() {
    if (!toast.isActive(toastId)) {
      toastInfo({
        title: translate('app.notifications.forum.welcome'),
        duration: null,
        isClosable: true,
        description: (
          <>
            {translate('app.notifications.forum.registered')}{' '}
            <Link isExternal href={process.env.REACT_APP_FORUM_GERTEC}>
              {process.env.REACT_APP_FORUM_GERTEC}
            </Link>
          </>
        )
      });
    }
  }

  function showToastCompleteAddress() {
    toastInfo({
      title: translate('app.notifications.formProfileUpdated.title'),
      duration: null,
      isClosable: true,
      description: translate('app.notifications.formProfileUpdated.description')
    });
  }

  if (isSignedIn) {
    return redirect();
  }

  if (authError) {
    return (
      <Flex
        minH="100vh"
        align="center"
        justify="center"
        direction="column"
        gap={4}
        px={6}
        textAlign="center"
      >
        <Text fontSize="lg" fontWeight="600">
          Nao foi possivel iniciar a autenticacao
        </Text>
        <Text maxW="480px">{authError}</Text>
        <Button onClick={() => window.location.reload()}>Tentar novamente</Button>
      </Flex>
    );
  }

  return <SpinnerLoading />;
};

export default UnAuthGuard;
